import React, { useState, useRef } from 'react'
import cn from 'classnames'
import api from '../../api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import useStore from 'state/knovStore'
import Button, { PurpleButton } from 'components/shared/buttons/Button'
import StreamAccessOptions from 'components/teams/StreamAccessOptions'
import StreamNotificationOptions from 'components/teams/StreamNotificationOptions'
import TeamLogoCropper from 'components/teams/TeamLogoCropper'
import styles from './create-stream-modal.module.scss'
import { uniqBy, defer } from 'lodash'
import { useNavigate } from 'react-router-dom'

export default function CreateStreamModal({ closeModal, panelId, filter }) {
    const navigate = useNavigate()
    const [streamName, setStreamName] = useState('')
    const [isPublic, setIsPublic] = useState(false) // Default to private
    const [notifyPeriod, setNotifyPeriod] = useState('instant_app') // Default to instant
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState('')
    const [showLogoCropper, setShowLogoCropper] = useState(false)
    const [selectedLogoImage, setSelectedLogoImage] = useState(null)
    const [logoBlob, setLogoBlob] = useState(null)

    const logoInputRef = useRef(null)

    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userStreams = useStore(state => state.userStreams)
    const set = useStore(state => state.set)

    const handleCreate = async () => {
        if (!streamName.trim()) {
            setError('Please enter a stream name')
            return
        }

        setIsCreating(true)
        setError('')

        try {
            // Create form data to match backend expectations
            const formData = new FormData()
            formData.append('name', streamName.trim())
            formData.append('space_id', activeSpaceId)
            formData.append('admin_id', gon.currentUser.id)
            formData.append('public', isPublic)
            formData.append('notify_period', notifyPeriod)

            // Add logo if one was selected
            if (logoBlob) {
                formData.append('team_image', logoBlob, 'logo.png')
            }

            // Use direct fetch like NewStream component
            const response = await fetch(gon.api.teams, {
                method: 'POST',
                body: formData,
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            })

            const output = await response.json()

            if (output?.team) {
                const newTeam = output.team
                const path = `/stream/${newTeam.id}`

                // Update user space options and store
                await api.updateUserSpaceOptions({
                    last_path: path,
                    filter: { team_ids: [newTeam.id] },
                })
                set({
                    userStreams: uniqBy([...userStreams, newTeam], s => s.id),
                    stream: newTeam,
                    streamId: newTeam.id,
                })

                // Navigate to the new stream
                defer(() => navigate(path))
                closeModal()
            } else {
                throw new Error('Failed to create stream')
            }
        } catch (error) {
            console.error('Error creating stream:', error)
            setError('Failed to create stream. Please try again.')
            setIsCreating(false)
        }
    }

    const handleLogoSelect = e => {
        const file = e.target.files[0]
        if (file && file.size > 5 * 1024 * 1024) {
            // 5MB limit for logos
            alert('File too large. Please select an image under 5MB.')
            e.target.value = ''
            return
        }

        if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedLogoImage(e.target.result)
                setShowLogoCropper(true)
            }
            reader.readAsDataURL(file)
        }
        e.target.value = ''
    }

    const handleLogoSave = croppedBlob => {
        setLogoBlob(croppedBlob)
        setShowLogoCropper(false)
    }

    return (
        <div className={styles.modalContent}>
            <div className={styles.header}>
                <h2>Create New Stream</h2>
                <p className={styles.subtitle}>
                    Publish threads to a stream whose visibility and membership you control.
                </p>
            </div>

            <div className={styles.formSection}>
                <label className={styles.label}>Stream Name</label>
                <input
                    type="text"
                    className={cn(styles.input, error && styles.inputError)}
                    placeholder="Enter stream name..."
                    value={streamName}
                    onChange={e => {
                        setStreamName(e.target.value)
                        setError('')
                    }}
                    autoFocus
                    maxLength={50}
                />
                <div className={styles.charCount}>{streamName.length}/50</div>
                {error && <div className={styles.error}>{error}</div>}
            </div>

            <div className={styles.logoSection}>
                <label className={styles.label}>Stream Logo (Optional)</label>
                <div className={styles.logoUploadArea} onClick={() => logoInputRef.current.click()}>
                    {logoBlob ? (
                        <div className={styles.logoPreview}>
                            <img src={URL.createObjectURL(logoBlob)} alt="Logo preview" />
                            <div className={styles.changeText}>Click to change</div>
                        </div>
                    ) : (
                        <div className={styles.logoPlaceholder}>
                            <i className="fa fa-camera" />
                            <span>Upload Logo</span>
                        </div>
                    )}
                </div>
                <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    style={{ display: 'none' }}
                />
            </div>

            <StreamAccessOptions isPublic={isPublic} onChange={setIsPublic} disabled={isCreating} />

            <StreamNotificationOptions
                notifyPeriod={notifyPeriod}
                onChange={setNotifyPeriod}
                disabled={isCreating}
            />

            <div className={styles.actions}>
                <Button text="Cancel" onClick={closeModal} disabled={isCreating} />
                <PurpleButton
                    text={isCreating ? 'Creating...' : 'Create Stream'}
                    onClick={handleCreate}
                    disabled={isCreating || !streamName.trim()}
                    inProgress={isCreating}
                />
            </div>

            {showLogoCropper && (
                <TeamLogoCropper
                    image={selectedLogoImage}
                    onSave={handleLogoSave}
                    onCancel={() => setShowLogoCropper(false)}
                />
            )}
        </div>
    )
}
